import { on } from '@lucid/core';
import { wishlist, WISHLIST_KEY } from '@lucid/wishlist';
import { addToCart } from '@lucid/card';

/** Rendered card markup per product handle, kept for the lifetime of the page. */
const cardCache = new Map();

/** @returns {string[]} */
function readHandles() {
  return wishlist.get();
}

/**
 * Wishlist drawer body. Reads the wishlist store (localStorage "lucid:wishlist", newest first, written by
 * lucid-wishlist.js), renders one product row per handle through the Section Rendering API (section
 * lucid-drawer-card) and keeps the list and the count in step with lucid:wishlist:change. Rows add to the cart
 * directly (size chips for products with variants); after an add the drawer closes so the cart drawer can open.
 */
class LucidWishlistDrawer extends HTMLElement {
  /** @type {Array<() => void>} */
  #cleanup = [];

  #dirty = true;

  /** @type {Promise<void> | null} */
  #rendering = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#cleanup.push(() => this.removeEventListener('click', this.#onClick));
    const drawer = this.closest('lucid-drawer');
    const onOpen = () => this.#render();
    drawer?.addEventListener('theme-drawer:open', onOpen);
    this.#cleanup.push(() => drawer?.removeEventListener('theme-drawer:open', onOpen));

    this.#cleanup.push(
      on('wishlist:change', (event) => {
        this.#dirty = true;
        const handles = Array.isArray(event.detail?.handles) ? event.detail.handles : readHandles();
        this.#updateCount(handles.length);
        if (event.detail?.added === false && event.detail.handle) this.#removeRow(event.detail.handle);
        if (drawer?.hasAttribute('open')) this.#render(handles);
      })
    );

    const onStorage = (event) => {
      if (event.key !== WISHLIST_KEY) return;
      this.#dirty = true;
      this.#updateCount(readHandles().length);
    };
    window.addEventListener('storage', onStorage);
    this.#cleanup.push(() => window.removeEventListener('storage', onStorage));

    this.#updateCount(readHandles().length);
  }

  disconnectedCallback() {
    this.#cleanup.forEach((off) => off());
    this.#cleanup = [];
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-wish-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      this.#toggleSizes(toggle);
      return;
    }
    const add = target?.closest('[data-wish-add]');
    if (add instanceof HTMLButtonElement) this.#add(add);
  };

  /** @param {HTMLButtonElement} toggle */
  #toggleSizes(toggle) {
    const sizes = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!sizes) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    for (const other of this.querySelectorAll('[data-wish-toggle][aria-expanded="true"]')) {
      if (other === toggle) continue;
      other.setAttribute('aria-expanded', 'false');
      const otherSizes = document.getElementById(other.getAttribute('aria-controls') || '');
      if (otherSizes) otherSizes.hidden = true;
    }
    toggle.setAttribute('aria-expanded', String(open));
    sizes.hidden = !open;
    if (open) sizes.querySelector('button')?.focus();
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const id = button.dataset.wishAdd;
    const row = button.closest('[data-wish-row]');
    if (!id || !row || row.hasAttribute('aria-busy')) return;
    const status = row.querySelector('[data-wish-status]');
    row.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    try {
      await addToCart([{ id, quantity: 1 }], { source: this, origin: 'lucid-wishlist-drawer' });
      /** @type {any} */ (this.closest('lucid-drawer'))?.close?.();
    } catch (error) {
      if (status) status.textContent = (error?.handled && error.message) || this.dataset.errorText || '';
    } finally {
      row.removeAttribute('aria-busy');
      button.removeAttribute('aria-busy');
    }
  }

  /** Removes a row right away and keeps focus inside the list. @param {string} handle */
  #removeRow(handle) {
    const list = this.#list;
    const item = list?.querySelector(`:scope > [data-handle="${CSS.escape(handle)}"]`);
    if (!list || !item) return;
    const hadFocus = item.contains(document.activeElement);
    const next = item.nextElementSibling || item.previousElementSibling;
    item.remove();
    if (hadFocus) {
      const target = next?.querySelector('.lucid-wish__button') || this.closest('lucid-drawer')?.querySelector('.theme-drawer__close-button, [data-drawer-close], button');
      if (target instanceof HTMLElement) target.focus();
    }
    if (!list.children.length) {
      list.hidden = true;
      const empty = this.#empty;
      if (empty) empty.hidden = false;
    }
  }

  get #list() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-list]'));
  }

  get #empty() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-empty]'));
  }

  get #loading() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-loading]'));
  }

  /** @param {number} count */
  #updateCount(count) {
    const drawer = this.closest('lucid-drawer');
    for (const element of drawer?.querySelectorAll('[data-wishlist-count]') ?? []) {
      element.textContent = count > 0 ? String(count) : '';
      element.toggleAttribute('hidden', count === 0);
    }
    const footer = drawer?.querySelector('[data-wishlist-footer]');
    if (footer instanceof HTMLElement) footer.hidden = count === 0;
  }

  /** @param {string[]} [handles] */
  async #render(handles = readHandles()) {
    if (!this.#dirty && !handles.some((handle) => !this.#list?.querySelector(`[data-handle="${CSS.escape(handle)}"]`))) {
      return;
    }
    if (this.#rendering) await this.#rendering;

    this.#rendering = this.#draw(handles).finally(() => {
      this.#rendering = null;
    });
    return this.#rendering;
  }

  /** @param {string[]} handles */
  async #draw(handles) {
    const list = this.#list;
    const empty = this.#empty;
    const loading = this.#loading;
    if (!list || !empty) return;

    const ordered = [...handles];
    this.#updateCount(ordered.length);

    if (!ordered.length) {
      list.replaceChildren();
      list.hidden = true;
      empty.hidden = false;
      if (loading) loading.hidden = true;
      this.#dirty = false;
      return;
    }

    empty.hidden = true;
    const missing = ordered.filter((handle) => !cardCache.has(handle));
    if (missing.length && loading && !list.children.length) {
      loading.hidden = false;
      list.hidden = true;
    }

    await Promise.all(missing.map((handle) => this.#fetchCard(handle)));

    const items = ordered
      .filter((handle) => cardCache.get(handle))
      .map((handle) => {
        const existing = list.querySelector(`:scope > [data-handle="${CSS.escape(handle)}"]`);
        if (existing) return existing;
        const item = document.createElement('li');
        item.className = 'lucid-wishlist-drawer__item';
        item.dataset.handle = handle;
        item.innerHTML = cardCache.get(handle) || '';
        return item;
      });

    list.replaceChildren(...items);
    list.hidden = items.length === 0;
    empty.hidden = items.length > 0;
    if (loading) loading.hidden = true;
    this.#dirty = false;
  }

  /** @param {string} handle */
  async #fetchCard(handle) {
    const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${this.dataset.section || 'lucid-drawer-card'}`;
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) {
        cardCache.set(handle, '');
        return;
      }
      const html = await response.text();
      const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-drawer-card]');
      cardCache.set(handle, card?.innerHTML.trim() || '');
    } catch {
      cardCache.delete(handle);
    }
  }
}

if (!customElements.get('lucid-wishlist-drawer')) {
  customElements.define('lucid-wishlist-drawer', LucidWishlistDrawer);
}
